import React, { useState, useCallback } from 'react';
import { useTrashcanItems, useRestoreTrashcanItem, useHardDeleteTrashcanItem, useBulkHardDeleteTrashcanItems } from '../hooks/useQueries';
import ListViewPage from '../components/Layout/ListViewPage';
import Modal from '../components/Popup/Modal';
import BulkActionsPopup from '../components/Popup/BulkActionsPopup';
import { formatDate } from '../utils/dateUtils';
import { formatItemRef } from '../utils/itemIdFormat';
import './trashcan.css';

const normalizeMultiFilter = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (value) return [value];
  return [];
};

/**
 * Trashcan Page Component
 * 
 * Displays all soft deleted items from items, tasks, and brainstorming tables.
 * Allows users to restore items or permanently delete them.
 * Shows days remaining until automatic cleanup.
 */
export default function TrashcanPage() {
  const [selectedItem, setSelectedItem] = useState(null);
  const [actionType, setActionType] = useState(null); // 'restore' or 'delete'
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState(() => normalizeMultiFilter(''));
  const [selectedRows, setSelectedRows] = useState(() => new Set());
  
  // React Query hooks
  const { data: trashcanItems = [], isLoading, error, refetch } = useTrashcanItems();
  const restoreMutation = useRestoreTrashcanItem();
  const hardDeleteMutation = useHardDeleteTrashcanItem();
  const bulkHardDeleteMutation = useBulkHardDeleteTrashcanItems();
  
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };
  
  const handleCloseToast = () => {
    setToast({ show: false, message: '', type: 'success' });
  };
  
  const handleRestore = (item) => {
    setSelectedItem(item);
    setActionType('restore');
  };
  
  const handlePermanentDelete = (item) => {
    setSelectedItem(item);
    setActionType('delete');
  };
  
  const confirmAction = async () => {
    if (!selectedItem || !actionType) return;
    
    try {
      if (actionType === 'restore') {
        await restoreMutation.mutateAsync({
          tableName: selectedItem.table_name,
          id: selectedItem.record_id
        });
        showToast(`${getItemTypeSingularLabel(selectedItem.table_name)} restored successfully`);
      } else {
        await hardDeleteMutation.mutateAsync({
          tableName: selectedItem.table_name,
          id: selectedItem.record_id
        });
        showToast(`${getItemTypeSingularLabel(selectedItem.table_name)} permanently deleted`);
      }
      
      refetch();
      setSelectedItem(null);
      setActionType(null);
    } catch (error) {
      showToast(`Failed to ${actionType} item`, 'error');
      console.error(`${actionType} error:`, error);
    }
  };
  
  const cancelAction = () => {
    setSelectedItem(null);
    setActionType(null);
  };

  // Bulk action handlers
  const handleBulkRestore = async (selectedIds) => {
    try {
      const selectedItems = trashcanItems.filter(item => selectedIds.has(item.id));
      
      for (const item of selectedItems) {
        await restoreMutation.mutateAsync({
          tableName: item.table_name,
          id: item.record_id
        });
      }
      
      showToast(`${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''} restored successfully`);
      setSelectedRows(new Set());
      refetch();
    } catch (error) {
      console.error('Bulk restore failed:', error);
      showToast('Bulk restore failed. Please try again.', 'error');
    }
  };

  const handleBulkDelete = async (selectedIds) => {
    try {
      const selectedItems = trashcanItems.filter(item => selectedIds.has(item.id));

      await bulkHardDeleteMutation.mutateAsync({
        items: selectedItems.map(item => ({
          tableName: item.table_name,
          id: item.record_id
        }))
      });
      
      showToast(`${selectedIds.size} item${selectedIds.size !== 1 ? 's' : ''} permanently deleted`);
      setSelectedRows(new Set());
      refetch();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      showToast('Bulk delete failed. Please try again.', 'error');
    }
  };

  // Safe selection change handler
  const handleSelectionChange = (newSelection) => {
    setSelectedRows(newSelection instanceof Set ? newSelection : new Set());
  };

  const handleClearSelection = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  // Define bulk actions
  const bulkActions = [
    {
      label: 'Restore',
      icon: 'bi bi-arrow-clockwise',
      className: 'btn btn-success',
      onClick: handleBulkRestore
    },
    {
      label: 'Delete Permanently',
      icon: 'bi bi-trash3',
      className: 'btn btn-danger',
      onClick: handleBulkDelete
    }
  ];
  
  const getItemTypeSingularLabel = (tableName) => {
    const labels = {
      items: 'Item',
      tasks: 'Task',
      brainstorming: 'Brainstorming Card'
    };
    return labels[tableName] || tableName;
  };
  
  // The trashcan holds rows from several tables. Only item ids are zero-padded;
  // tasks and brainstorming cards keep their own numbering.
  const formatRecordRef = (record) => (
    record?.table_name === 'items'
      ? formatItemRef(record.record_id)
      : `#${record?.record_id}`
  );

  const getItemTitle = (item) => {
    const data = item.record_data;
    return data.title || data.description || `${getItemTypeSingularLabel(item.table_name)} ${formatRecordRef(item)}`;
  };
  
  const getItemDescription = (item) => {
    const data = item.record_data;
    if (item.table_name === 'items') {
      return data.description || 'No description';
    } else if (item.table_name === 'tasks') {
      return data.description || 'No description';
    } else if (item.table_name === 'brainstorming') {
      return data.description || 'No description';
    }
    return 'No description available';
  };

  // Define columns for the trashcan list view
  const columns = [
    {
      key: 'type',
      label: 'Type',
      sortable: true,
      render: (item) => (
        <span className="item-type-badge" style={{ 
          padding: '4px 8px', 
          borderRadius: '4px', 
          fontSize: '0.75rem',
          fontWeight: '500',
          backgroundColor: item.table_name === 'items' ? '#dbeafe' : 
                          item.table_name === 'tasks' ? '#dcfce7' : '#fef3c7',
          color: item.table_name === 'items' ? '#1e40af' : 
                 item.table_name === 'tasks' ? '#16a34a' : '#d97706'
        }}>
          {getItemTypeSingularLabel(item.table_name)}
        </span>
      )
    },
    {
      key: 'title',
      label: 'Title',
      sortable: true,
      render: (item) => (
        <div>
          <div className="item-title" style={{ fontWeight: '500' }}>
            {getItemTitle(item)}
          </div>
          <div className="item-id" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            {formatRecordRef(item)}
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      sortable: false,
      render: (item) => (
        <div className="item-description" style={{ 
          maxWidth: '300px', 
          overflow: 'hidden', 
          textOverflow: 'ellipsis',
          fontSize: '0.875rem',
          color: '#6b7280'
        }}>
          {getItemDescription(item)}
        </div>
      )
    },
    {
      key: 'deleted_at',
      label: 'Deleted',
      sortable: true,
      render: (item) => (
        <div>
          <div style={{ fontSize: '0.875rem' }}>
            {formatDate(item.deleted_at)}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
            by {item.deleted_by || 'System'}
          </div>
        </div>
      )
    },
    {
      key: 'cleanup',
      label: 'Auto-cleanup',
      sortable: true,
      render: (item) => (
        <div className={`cleanup-status ${item.days_until_cleanup <= 3 ? 'urgent' : ''}`} style={{
          fontSize: '0.875rem',
          fontWeight: '500',
          color: item.days_until_cleanup <= 3 ? '#dc2626' : '#16a34a'
        }}>
          {item.days_until_cleanup > 0 
            ? `${item.days_until_cleanup} day${item.days_until_cleanup !== 1 ? 's' : ''}`
            : 'Pending'
          }
        </div>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      sortable: false,
      width: '180px',
      render: (item) => (
        <div className="action-buttons" style={{ display: 'flex', gap: '8px' }}>
          <button 
            className="btn btn-sm btn-success"
            onClick={(e) => {
              e.stopPropagation();
              handleRestore(item);
            }}
            title="Restore item"
            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
          >
            <i className="bi bi-arrow-counterclockwise"></i>
            Restore
          </button>
          <button 
            className="btn btn-sm btn-danger"
            onClick={(e) => {
              e.stopPropagation();
              handlePermanentDelete(item);
            }}
            title="Permanently delete item"
            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
          >
            <i className="bi bi-trash-fill"></i>
            Delete
          </button>
        </div>
      )
    }
  ];

  // Filter configuration for the filter sidebar
  const filters = [
    {
      icon: () => <i className="bi bi-list"></i>,
      value: [
        { value: 'items', label: 'Items' },
        { value: 'tasks', label: 'Tasks' },
        { value: 'brainstorming', label: 'Brainstorming Cards' }
      ].filter(option => typeFilter.includes(option.value)),
      onChange: (selectedOptions) => setTypeFilter(Array.isArray(selectedOptions) ? selectedOptions.map(opt => opt.value) : []),
      options: [
        { value: 'items', label: 'Items' },
        { value: 'tasks', label: 'Tasks' },
        { value: 'brainstorming', label: 'Brainstorming Cards' }
      ],
      placeholder: 'All Types',
      isClearable: true,
      isMulti: true,
      containerStyle: { maxWidth: '300px' } // Make it wider
    }
  ];

  // Custom filtering logic
  const customFilter = (data) => {
    let filtered = data;

    // Apply search filter on title and description
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const title = getItemTitle(item).toLowerCase();
        const description = getItemDescription(item).toLowerCase();
        return title.includes(searchLower) || description.includes(searchLower);
      });
    }

    // Apply type filter
    if (typeFilter.length > 0) {
      const allowedTypes = new Set(typeFilter);
      filtered = filtered.filter(item => allowedTypes.has(item.table_name));
    }

    return filtered;
  };

  return (
    <>
    <ListViewPage
      title="Trash"
      itemLabel="deleted items"
      
      // Data
      activeData={trashcanItems}
      completedData={[]} // No completed section needed
      loading={isLoading}
      error={error}
      
      // Columns
      columns={columns}
      defaultVisibleColumns={['type', 'title', 'description', 'deleted_at', 'cleanup', 'actions']}
      
      // Search
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder="Search deleted items..."
      
      // Filters
      filters={filters}
      customFilter={customFilter}
      
      // Settings
      showCompletedByDefault={false}
      completedSectionTitle="Archive"
      
      // Toast notifications
      toast={toast}
      onCloseToast={handleCloseToast}

      // Multi-select functionality
      enableMultiSelect={true}
      selectedRows={selectedRows}
      onSelectionChange={handleSelectionChange}
      selectableRowKey="id"
      
      additionalModals={(
        <Modal
          show={!!selectedItem && !!actionType}
          onCancel={cancelAction}
          onConfirm={confirmAction}
          title={actionType === 'restore' ? 'Restore Item' : 'Permanently Delete Item'}
          confirmText={actionType === 'restore' ? 'Restore' : 'Delete Permanently'}
          confirmBtnClass={actionType === 'restore' ? 'btn-success' : 'btn-danger'}
          loading={restoreMutation.isPending || hardDeleteMutation.isPending}
          content={
            selectedItem ? (
              <div>
                <p>
                  Are you sure you want to {actionType === 'restore' ? 'restore' : 'permanently delete'} this {getItemTypeSingularLabel(selectedItem.table_name).toLowerCase()}?
                </p>
                <div className="modal-item-preview" style={{
                  padding: '1rem',
                  backgroundColor: '#f8fafc',
                  borderRadius: '6px',
                  margin: '1rem 0'
                }}>
                  <strong>{getItemTitle(selectedItem)}</strong>
                </div>
                {actionType === 'delete' && (
                  <p className="warning-text" style={{ color: '#dc2626', marginTop: '1rem' }}>
                    <i className="bi bi-exclamation-triangle"></i>
                    This action cannot be undone.
                  </p>
                )}
              </div>
            ) : null
          }
        />
      )}
    />

    {/* Bulk Actions Popup */}
    <BulkActionsPopup
      selectedRows={selectedRows}
      bulkActions={bulkActions}
      onClose={handleClearSelection}
      itemLabel="deleted item"
    />
    </>
  );
}